// src/layouts/documents/DocumentViewer.js

import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import Loader from "components/Loader/Loader";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  TextField,
  Chip,
} from "@mui/material";
import { useSnackbar } from "context/SnackbarContext";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";

import { Document, Page, pdfjs } from "react-pdf";
import { IconButton, Tabs, Tab, Box, Grid, Card, Tooltip, Select, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ArrowBack,
  ArrowForward,
  ZoomIn,
  ZoomOut,
  Delete,
  Download,
  Print,
  CheckCircle,
  Save,
} from "@mui/icons-material";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Import your API instance
import api from "../../services/api";

// Import React Query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import InvoiceDetails from "common/DocumentViewer/InvoiceDetails/InvoiceDetails";
import InvoiceLineItems from "common/DocumentViewer/InvoiceLineItems/InvoiceLineItems";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function DocumentViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { selectedCompany } = useCompany(); // Pridėkite šią eilutę čia
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  // Local state for document data
  const [documentData, setDocumentData] = useState(null);
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const hasNotes = Boolean(notes.trim());

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailsPaneWidth, setDetailsPaneWidth] = useState(() => {
    const savedWidth = Number(window.localStorage.getItem("document-details-pane-width"));
    return savedWidth >= 32 && savedWidth <= 68 ? (savedWidth === 42 ? 50 : savedWidth) : 50;
  });
  const splitContainerRef = useRef(null);
  const [isSavingAndNext, setIsSavingAndNext] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [dirtySections, setDirtySections] = useState({
    details: false,
    notes: false,
    lineItems: false,
  });
  const hasUnsavedChanges = Object.values(dirtySections).some(Boolean);
  const navigationDocumentIds = useMemo(() => {
    if (!selectedCompany?.id) return [];
    try {
      const saved = JSON.parse(
        window.sessionStorage.getItem(`documentNavigation:${selectedCompany.id}`) || "{}"
      );
      return Array.isArray(saved.documentIds) ? saved.documentIds.map(Number) : [];
    } catch (error) {
      return [];
    }
  }, [selectedCompany?.id, id]);

  const setSectionDirty = (section, isDirty) => {
    setDirtySections((previous) => ({ ...previous, [section]: isDirty }));
  };

  const confirmNavigation = (destination) => {
    if (
      hasUnsavedChanges &&
      !window.confirm(
        t("unsavedChangesConfirm") ||
          "You have unsaved changes. Do you want to leave this document?"
      )
    ) {
      return;
    }
    navigate(destination);
  };

  useEffect(() => {
    const warnBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    setDirtySections({ details: false, notes: false, lineItems: false });
  }, [id]);

  // Zoom and page control functions
  const handleZoomIn = () => setZoom((prevZoom) => Math.min(prevZoom + 0.1, 3.0));
  const handleZoomOut = () => setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  const goToPrevPage = () => setPageNumber((prevPageNumber) => Math.max(prevPageNumber - 1, 1));
  const goToNextPage = () =>
    setPageNumber((prevPageNumber) => Math.min(prevPageNumber + 1, numPages));

  const updateDetailsPaneWidth = (nextWidth) => {
    const roundedWidth = Math.round(Math.min(68, Math.max(32, nextWidth)));
    setDetailsPaneWidth(roundedWidth);
    window.localStorage.setItem("document-details-pane-width", String(roundedWidth));
    window.requestAnimationFrame(handleResize);
  };

  const handlePaneResize = (event) => {
    const divider = event.currentTarget;
    if (!divider.hasPointerCapture(event.pointerId) || !splitContainerRef.current) return;
    const bounds = splitContainerRef.current.getBoundingClientRect();
    updateDetailsPaneWidth(((event.clientX - bounds.left) / bounds.width) * 100);
  };

  const handlePaneResizeKeyDown = (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    updateDetailsPaneWidth(detailsPaneWidth + (event.key === "ArrowLeft" ? -2 : 2));
  };

  // Function to handle container size
  const handleResize = () => {
    const container = document.getElementById("pdf-container");
    if (container) {
      setContainerWidth(container.clientWidth);
    }
  };

  // Listen to window resize events
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    // Also try to set it immediately and on a small timeout to catch the render
    handleResize();
    setTimeout(handleResize, 100);
    setTimeout(handleResize, 500);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    handleResize(); // <-- Catch the width immediately when the PDF container appears!
  };

  // UseQuery to fetch document data
  const { data, isLoading, error } = useQuery({
    queryKey: ["document", id],
    queryFn: async () => {
      try {
        console.log(`Fetching document data for ID: ${id}`);
        const response = await api.get(`/api/documents/${id}/`);
        console.log("Document data fetched:", response.data);
        return response.data;
      } catch (err) {
        console.error("Error fetching document data:", err);
        throw err;
      }
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
  });

  // Update local state when data changes
  useEffect(() => {
    if (data) {
      console.log("Updating state with document data:", data);
      setDocumentData({
        ...data,
        data: data.data || {},
      });
      setNotes(data.notes || "");
      setLineItems(data.line_items || []);
    }
  }, [data]);

  useEffect(() => {
    setSaveFeedback(null);
  }, [id]);

  // Mutation: Save Notes
  const saveNotesMutation = useMutation({
    mutationFn: async (newNotes) => {
      console.log("Saving notes:", newNotes);
      const response = await api.post(`/api/documents/${id}/save_notes/`, { notes: newNotes });
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("notesSavedSuccessfully"), "success");
      setSectionDirty("notes", false);
      setSaveFeedback({ status: "success", label: t("saved") || "Saved" });
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving notes:", error);
      showSnackbar(t("errorSavingNotes"), "error");
      setSaveFeedback({ status: "error", label: t("errorSavingNotes") || "Save failed" });
    },
  });

  // Mutation: Save Details
  const saveDetailsMutation = useMutation({
    mutationFn: async (updatedData) => {
      console.log("Saving document details:", updatedData);

      const formattedData = { ...updatedData };
      const amountFields = ["net_amount", "total_amount", "total_tax_amount"];
      amountFields.forEach((field) => {
        if (formattedData[field] && !isNaN(parseFloat(formattedData[field]))) {
          formattedData[field] = parseFloat(formattedData[field]).toFixed(2);
        }
      });

      const response = await api.post(`/api/documents/${id}/update-data/`, formattedData);
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("informationSavedSuccessfully"), "success");
      setSectionDirty("details", false);
      setSaveFeedback({ status: "success", label: t("saved") || "Saved" });
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving document details:", error);
      showSnackbar(t("errorSavingInformation"), "error");
      setSaveFeedback({ status: "error", label: t("errorSavingInformation") || "Save failed" });
    },
  });

  // Mutation: Save Line Items
  const saveLineItemsMutation = useMutation({
    mutationFn: async (updatedLineItems) => {
      console.log("Saving line items:", updatedLineItems);

      const parseNumber = (val) => {
        if (val === null || val === undefined || val === "") return null;
        const parsed = parseFloat(String(val).replace(",", "."));
        return Number.isFinite(parsed) ? parsed : null;
      };

      const formattedLineItems = (updatedLineItems || []).map((item) => {
        const qty = parseNumber(item.quantity);
        // Keep the decimal string intact so saving does not lose price precision.
        const price = String(item.unit_price ?? "")
          .replace(",", ".")
          .trim();
        const total = parseNumber(item.total_amount);

        return {
          sku: item.sku !== null && item.sku !== undefined ? String(item.sku) : "",
          description:
            item.description !== null && item.description !== undefined
              ? String(item.description)
              : "",
          unit_of_measure:
            item.unit_of_measure !== null && item.unit_of_measure !== undefined
              ? String(item.unit_of_measure)
              : "",
          quantity: qty !== null ? String(qty) : "0.0000",
          unit_price: price || "0.0000",
          total_amount: total !== null ? total.toFixed(2) : null,
          vat_percentage:
            item.vat_percentage !== null &&
            item.vat_percentage !== undefined &&
            item.vat_percentage !== ""
              ? String(item.vat_percentage)
              : null,
          line_type: item.line_type || "item",
        };
      });

      const response = await api.post(`/api/documents/${id}/update-line-items/`, {
        line_items: formattedLineItems,
      });
      return response.data;
    },
    onSuccess: (data) => {
      showSnackbar(t("lineItemsSavedSuccessfully"), "success");
      setSectionDirty("lineItems", false);
      setSaveFeedback({ status: "success", label: t("saved") || "Saved" });

      // Jei backend grąžino atnaujintą dokumentą, atnaujinkime cache
      if (data.document) {
        queryClient.setQueryData(["document", id], data.document);
        // Atnaujinkime ir local state
        setDocumentData({
          ...data.document,
          data: data.document.data || {},
        });
        setLineItems(data.document.line_items || []);
      } else {
        // Jei ne, tiesiog invaliduokime
        queryClient.invalidateQueries(["document", id]);
      }

      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error saving line items:", error);
      showSnackbar(t("errorSavingLineItems"), "error");
      setSaveFeedback({ status: "error", label: t("errorSavingLineItems") || "Save failed" });
    },
  });

  // Mutation: Recognize Document
  const recognizeMutation = useMutation({
    mutationFn: async () => {
      console.log("Recognizing document with ID:", id);
      const response = await api.post(`/api/recognize/`, {
        document_ids: [id],
        force_recognize: true,
      });
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("documentRecognizedSuccessfully"), "success");
      queryClient.invalidateQueries(["document", id]); // Atnaujina dabartinį dokumentą
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error recognizing document:", error);
      showSnackbar(t("errorRecognizingDocument"), "error");
    },
  });

  // Mutation: Delete Document
  const deleteMutation = useMutation({
    mutationFn: async (documentId) => {
      console.log("Deleting document with ID:", documentId);
      const response = await api.delete(`/api/documents/${documentId}/`);
      return response.data;
    },
    onSuccess: () => {
      showSnackbar(t("documentDeletedSuccessfully"), "success");
      setDeleteTarget(null);
      navigate("/documents");
      queryClient.invalidateQueries(["documents", selectedCompany?.id]); // Atnaujina dokumentų sąrašą
    },
    onError: (error) => {
      console.error("Error deleting document:", error);
      showSnackbar(t("errorDeletingDocument"), "error");
    },
  });

  // Download document (no mutation needed)
  const handleDownload = async () => {
    console.log("Downloading document:", documentData.file_url);

    let downloadName = documentData.title || "document";
    const knownExtensions = [".pdf", ".jpg", ".jpeg", ".png"];
    const hasKnownExtension = knownExtensions.some((ext) =>
      downloadName.toLowerCase().endsWith(ext)
    );

    if (!hasKnownExtension) {
      downloadName += ".pdf"; // Pridedame .pdf jei pabaigoje nėra oficialaus formato
    }

    try {
      // Fetching the file as a Blob to enforce the custom filename.
      // `link.download` attribute is ignored by browsers if the file is cross-origin.
      const response = await fetch(documentData.file_url);
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = downloadName; // Dabar naršyklė garantuotai naudos šį pavadinimą
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.warn("Fetch missing CORS or failed, falling back to basic link.", error);
      const link = document.createElement("a");
      link.href = documentData.file_url;
      link.download = downloadName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Print document (no mutation needed)
  const handlePrint = () => {
    console.log("Printing document:", documentData.file_url);
    const printWindow = window.open(documentData.file_url, "_blank");
    printWindow.focus();
    printWindow.print();
  };

  // Reset PDF viewer state every time the document ID changes so react-pdf
  // starts clean and never gets stuck on the previous document's "Loading page…".
  // If react-query already has this document cached, populate documentData
  // synchronously so the full-screen Loader never flashes for cached docs.
  // Only set documentData=null (→ Loader) when the doc genuinely isn't cached yet.
  useEffect(() => {
    const cached = queryClient.getQueryData(["document", id]);
    if (cached) {
      setDocumentData({
        ...cached,
        data: cached.data || {},
      });
      setNotes(cached.notes || "");
      setLineItems(cached.line_items || []);
    } else {
      setDocumentData(null);
    }
    setNumPages(null);
    setPageNumber(1);
  }, [id]);

  // Dependencies: query status
  if (error) {
    console.error("Error in DocumentViewer:", error);
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <MDTypography variant="h4" color="error">
            Error fetching document data
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (isLoading || !documentData) {
    console.log("Loading document data...");
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <Loader fullscreen />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  console.log("DocumentViewer - Document data is valid:", documentData);

  const zoomPresets = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0];
  const zoomOptions = [...new Set([...zoomPresets, zoom])].sort((a, b) => a - b);
  const uploaderDisplay =
    documentData.uploaded_by_name || documentData.uploaded_by_email || t("unknownUploader");
  const rawRecognizedText = documentData.recognized_text;
  const navigationIndex = navigationDocumentIds.indexOf(Number(id));
  const hasDocumentNavigation = navigationIndex >= 0;
  const prevDocumentId = hasDocumentNavigation
    ? navigationDocumentIds[navigationIndex - 1] || null
    : documentData.prev_document_id;
  const nextDocumentId = hasDocumentNavigation
    ? navigationDocumentIds[navigationIndex + 1] || null
    : documentData.next_document_id;

  const handleSaveAndNext = async () => {
    if (!nextDocumentId || !hasUnsavedChanges || isSavingAndNext) return;
    setIsSavingAndNext(true);
    try {
      if (dirtySections.details) await saveDetailsMutation.mutateAsync(documentData.data);
      if (dirtySections.notes) await saveNotesMutation.mutateAsync(notes);
      if (dirtySections.lineItems) await saveLineItemsMutation.mutateAsync(lineItems);
      navigate(`/documents/${nextDocumentId}`);
    } catch (error) {
      // Individual mutations already show a specific error message and retain the draft.
    } finally {
      setIsSavingAndNext(false);
    }
  };

  const handleSaveAll = async () => {
    if (!hasUnsavedChanges || isSavingDocument) return;
    try {
      if (dirtySections.details) await saveDetailsMutation.mutateAsync(documentData.data);
      if (dirtySections.notes) await saveNotesMutation.mutateAsync(notes);
      if (dirtySections.lineItems) await saveLineItemsMutation.mutateAsync(lineItems);
    } catch (error) {
      // Individual mutations already handle their own errors and keep draft
    }
  };

  const hasRecognizedText =
    typeof rawRecognizedText === "string"
      ? rawRecognizedText.trim() !== "" && rawRecognizedText.trim().toLowerCase() !== "false"
      : Boolean(rawRecognizedText);
  const recognizedTextToShow = hasRecognizedText
    ? rawRecognizedText
    : t("noRecognizedTextAvailable");
  const isSavingDocument =
    saveNotesMutation.isPending || saveDetailsMutation.isPending || saveLineItemsMutation.isPending;

  const saveStatusChip = isSavingDocument ? (
    <Chip label={t("saving") || "Saving..."} size="small" color="info" />
  ) : saveFeedback ? (
    <Chip
      label={saveFeedback.label}
      size="small"
      color={saveFeedback.status === "error" ? "error" : "success"}
    />
  ) : null;

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <MDBox p={{ xs: 2, md: 3 }}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={isMdDown ? "flex-start" : "center"}
                  flexDirection={isMdDown ? "column" : "row"}
                  gap={isMdDown ? 1 : 0}
                  mb={3}
                  pb={2}
                  sx={{ minWidth: 0, borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <MDBox display="flex" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      onClick={() => confirmNavigation("/documents")}
                      startIcon={<ArrowBack />}
                    >
                      {t("backToDocuments")}
                    </MDButton>
                    {!isMdDown && (
                      <>
                        <MDTypography
                          variant="h5"
                          fontWeight="medium"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            maxWidth: "50vw",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={documentData.title || ""}
                        >
                          {documentData.title}
                        </MDTypography>
                        {hasUnsavedChanges && (
                          <Chip
                            label={t("unsavedChanges") || "Unsaved changes"}
                            size="small"
                            color="warning"
                          />
                        )}
                        {saveStatusChip}
                      </>
                    )}
                  </MDBox>
                  <Box sx={{ flex: 1, mr: "auto", ml: isMdDown ? 0 : 2, mb: isMdDown ? 1 : 0 }} />
                  {isMdDown && (
                    <MDTypography
                      variant="subtitle1"
                      fontWeight="medium"
                      sx={{
                        width: "100%",
                        mt: 0.5,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                      title={documentData.title || ""}
                    >
                      {documentData.title}
                      {hasUnsavedChanges && (
                        <Chip
                          label={t("unsavedChanges") || "Unsaved changes"}
                          size="small"
                          color="warning"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {saveStatusChip}
                    </MDTypography>
                  )}
                  <MDBox display="flex" gap={1} flexWrap={isMdDown ? "wrap" : "nowrap"}>
                    {hasUnsavedChanges && (
                      <>
                        <MDButton
                          variant="contained"
                          color="success"
                          size="small"
                          disabled={isSavingDocument}
                          onClick={handleSaveAll}
                          startIcon={<Save />}
                        >
                          {isSavingDocument ? t("saving") || "Saving..." : t("save") || "Save"}
                        </MDButton>
                        {nextDocumentId && (
                          <MDButton
                            variant="contained"
                            color="info"
                            size="small"
                            disabled={isSavingAndNext || isSavingDocument}
                            onClick={handleSaveAndNext}
                          >
                            {isSavingAndNext
                              ? t("saving") || "Saving..."
                              : t("saveAndNext") || "Save and next"}
                          </MDButton>
                        )}
                      </>
                    )}
                    <Tooltip title={t("previousDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <IconButton
                          color="info"
                          disabled={!prevDocumentId}
                          onClick={() =>
                            prevDocumentId && confirmNavigation(`/documents/${prevDocumentId}`)
                          }
                        >
                          <ArrowBack />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("nextDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <IconButton
                          color="info"
                          disabled={!nextDocumentId}
                          onClick={() =>
                            nextDocumentId && confirmNavigation(`/documents/${nextDocumentId}`)
                          }
                        >
                          <ArrowForward />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("recognizeDocument")}>
                      <span style={{ display: "inline-flex" }}>
                        <MDButton
                          variant="contained"
                          color="info"
                          onClick={() => recognizeMutation.mutate()}
                          disabled={recognizeMutation.isPending}
                          startIcon={<CheckCircle />}
                        >
                          {recognizeMutation.isPending ? t("processing") : t("recognize")}
                        </MDButton>
                      </span>
                    </Tooltip>
                  </MDBox>
                </MDBox>

                <Grid
                  container
                  spacing={2}
                  ref={splitContainerRef}
                  sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{
                      width: { md: `${detailsPaneWidth}%` },
                      flexBasis: { md: `${detailsPaneWidth}%` },
                      maxWidth: { md: `${detailsPaneWidth}%` },
                    }}
                  >
                    <Card
                      sx={{
                        height: "100%",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "none",
                        overflow: "hidden",
                      }}
                    >
                      <MDBox p={2}>
                        {isMdDown ? (
                          <Select
                            value={tabValue}
                            onChange={(e) => setTabValue(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{ mb: 2 }}
                            MenuProps={{
                              disableScrollLock: true,
                              PaperProps: { sx: { maxHeight: 360 } },
                            }}
                          >
                            <MenuItem value={0}>{t("documentDetails")}</MenuItem>
                            <MenuItem value={3}>{t("lineItems")}</MenuItem>
                            <MenuItem value={2}>{t("notes")}</MenuItem>
                            <MenuItem value={1}>{t("recognizedText")}</MenuItem>
                            <MenuItem value={4}>{t("formattedText")}</MenuItem>
                          </Select>
                        ) : (
                          <Tabs
                            value={tabValue}
                            onChange={(event, newValue) => setTabValue(newValue)}
                            variant="scrollable"
                            sx={{
                              borderBottom: 1,
                              borderColor: "divider",
                              mb: 2,
                            }}
                          >
                            <Tab value={0} label={t("documentDetails")} />
                            <Tab value={3} label={t("lineItems")} />
                            <Tab
                              value={2}
                              label={
                                <Box
                                  component="span"
                                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
                                >
                                  {t("notes")}
                                  {hasNotes && (
                                    <Box
                                      component="span"
                                      aria-label={t("notes")}
                                      sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: "50%",
                                        backgroundColor: "warning.main",
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                            />
                            <Tab value={1} label={t("recognizedText")} />
                            <Tab value={4} label={t("formattedText")} />
                          </Tabs>
                        )}

                        <MDBox
                          sx={{
                            height: isMdDown ? "auto" : "calc(100vh - 280px)",
                            maxHeight: isMdDown ? "none" : "calc(100vh - 280px)",
                            overflowY: isMdDown ? "visible" : "auto",
                          }}
                        >
                          {tabValue === 0 && (
                            <MDBox>
                              <InvoiceDetails
                                documentData={documentData}
                                setDocumentData={(nextDocumentData) => {
                                  setDocumentData(nextDocumentData);
                                  setSectionDirty("details", true);
                                }}
                                handleDetailsSave={() => {
                                  saveDetailsMutation.mutate(documentData.data);
                                }}
                                isSaving={saveDetailsMutation.isPending}
                              />
                            </MDBox>
                          )}

                          {tabValue === 1 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("recognizedText")}
                              </MDTypography>
                              <Box
                                sx={{
                                  backgroundColor: "background.paper",
                                  p: 2,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: "divider",
                                  mb: documentData?.tables?.length > 0 ? 3 : 0,
                                }}
                              >
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontFamily: "inherit",
                                    margin: 0,
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {recognizedTextToShow}
                                </pre>
                              </Box>
                              {documentData?.tables?.length > 0 && (
                                <MDBox>
                                  <MDTypography variant="h6" mb={1}>
                                    {t("extractedTables")}
                                  </MDTypography>
                                  {documentData.tables.map((table, tableIndex) => (
                                    <Box key={tableIndex} sx={{ mb: 2, overflowX: "auto" }}>
                                      <MDTypography
                                        variant="caption"
                                        color="text"
                                        mb={0.5}
                                        display="block"
                                      >
                                        {t("table")} {tableIndex + 1}
                                      </MDTypography>
                                      <table
                                        style={{
                                          borderCollapse: "collapse",
                                          width: "100%",
                                          fontSize: "0.8rem",
                                        }}
                                      >
                                        <tbody>
                                          {table.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                              {Array.isArray(row) ? (
                                                row.map((cell, cellIndex) => (
                                                  <td
                                                    key={cellIndex}
                                                    style={{
                                                      border: "1px solid #ccc",
                                                      padding: "4px 8px",
                                                      backgroundColor:
                                                        rowIndex === 0 ? "#f5f5f5" : "transparent",
                                                      fontWeight: rowIndex === 0 ? 600 : 400,
                                                      whiteSpace: "pre-wrap",
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {cell}
                                                  </td>
                                                ))
                                              ) : (
                                                <td
                                                  style={{
                                                    border: "1px solid #ccc",
                                                    padding: "4px 8px",
                                                  }}
                                                >
                                                  {row}
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </Box>
                                  ))}
                                </MDBox>
                              )}
                            </MDBox>
                          )}

                          {tabValue === 2 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("notes")}
                              </MDTypography>
                              <Card sx={{ mb: 2 }}>
                                <MDBox p={2}>
                                  <MDTypography variant="body2" color="text" fontWeight="medium">
                                    {t("uploadedBy")}: {uploaderDisplay}
                                  </MDTypography>
                                </MDBox>
                              </Card>
                              <TextField
                                label={t("notes")}
                                value={notes}
                                onChange={(e) => {
                                  setNotes(e.target.value);
                                  setSectionDirty("notes", true);
                                }}
                                fullWidth
                                multiline
                                rows={6}
                                variant="outlined"
                                sx={{ mb: 2 }}
                              />
                              <MDButton
                                variant="contained"
                                color="info"
                                onClick={() => saveNotesMutation.mutate(notes)}
                                startIcon={<CheckCircle />}
                                disabled={saveNotesMutation.isPending}
                              >
                                {saveNotesMutation.isPending
                                  ? t("saving") || "Saving..."
                                  : t("saveNotes")}
                              </MDButton>
                            </MDBox>
                          )}

                          {tabValue === 3 && (
                            <MDBox>
                              <MDTypography variant="h6" mb={2}>
                                {t("lineItems")}
                              </MDTypography>
                              <InvoiceLineItems
                                lineItems={lineItems}
                                onDirtyChange={() => setSectionDirty("lineItems", true)}
                                onItemsChange={setLineItems}
                                isSaving={saveLineItemsMutation.isPending}
                                onSave={(updatedLineItems) => {
                                  setLineItems(updatedLineItems);
                                  saveLineItemsMutation.mutate(updatedLineItems);
                                }}
                                discountAmount={
                                  documentData?.data?.discount_amount ||
                                  documentData?.discount_amount
                                }
                                discountPercentage={
                                  documentData?.data?.discount_percentage ||
                                  documentData?.discount_percentage
                                }
                                financialAdjustments={
                                  documentData?.data?.financial_adjustments || []
                                }
                                netAmount={
                                  documentData?.data?.net_amount ?? documentData?.net_amount
                                }
                              />
                            </MDBox>
                          )}

                          {tabValue === 4 && (
                            <MDBox>
                              {documentData?.data && (
                                <Card sx={{ mb: 2 }}>
                                  <MDBox p={2}>
                                    <MDTypography variant="h6" mb={1}>
                                      {t("tokenUsage")}
                                    </MDTypography>
                                    <MDTypography variant="body2">
                                      {t("promptTokens")}: {documentData.data.prompt_tokens || 0}
                                    </MDTypography>
                                    <MDTypography variant="body2">
                                      {t("completionTokens")}:{" "}
                                      {documentData.data.completion_tokens || 0}
                                    </MDTypography>
                                    <MDTypography variant="body2" fontWeight="bold">
                                      {t("totalTokens")}: {documentData.data.total_tokens || 0}
                                    </MDTypography>
                                  </MDBox>
                                </Card>
                              )}
                              <MDTypography variant="h6" mb={2}>
                                {t("formattedText")}
                              </MDTypography>
                              <Box
                                sx={{
                                  backgroundColor: "background.paper",
                                  p: 2,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontFamily: "inherit",
                                    margin: 0,
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {documentData?.formatted_text || t("noFormattedTextAvailable")}
                                </pre>
                              </Box>
                            </MDBox>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>

                  {!isMdDown && (
                    <Box
                      role="separator"
                      aria-label={t("resizeDocumentPanels") || "Resize document panels"}
                      aria-orientation="vertical"
                      aria-valuemin={32}
                      aria-valuemax={68}
                      aria-valuenow={detailsPaneWidth}
                      tabIndex={0}
                      onPointerDown={(event) =>
                        event.currentTarget.setPointerCapture(event.pointerId)
                      }
                      onPointerMove={handlePaneResize}
                      onKeyDown={handlePaneResizeKeyDown}
                      sx={{
                        width: 12,
                        mx: -1,
                        cursor: "col-resize",
                        touchAction: "none",
                        position: "relative",
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: 2,
                        },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: "15%",
                          bottom: "15%",
                          left: "50%",
                          borderLeft: "2px solid",
                          borderColor: "divider",
                        },
                      }}
                    />
                  )}
                  <Grid item xs={12} sx={{ flex: { md: 1 }, minWidth: 0 }}>
                    <Card
                      sx={{
                        height: "100%",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "none",
                        overflow: "hidden",
                      }}
                    >
                      <MDBox p={2}>
                        <MDTypography variant="h6" mb={2}>
                          {t("documentPreview")}
                        </MDTypography>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                          sx={{
                            backgroundColor: "background.paper",
                            p: 1,
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            flexWrap: isMdDown ? "wrap" : "nowrap",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <Tooltip title={t("zoomOut")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={handleZoomOut}
                                  size="small"
                                  disabled={zoom <= 0.5}
                                >
                                  <ZoomOut />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Select
                              value={zoom}
                              onChange={(e) => setZoom(e.target.value)}
                              size="small"
                              sx={{
                                minWidth: 90,
                                height: 32,
                                "& .MuiSelect-select": {
                                  paddingTop: "6px",
                                  paddingBottom: "6px",
                                  paddingLeft: "10px",
                                },
                              }}
                              MenuProps={{ disableScrollLock: true }}
                            >
                              {zoomOptions.map((z) => (
                                <MenuItem key={z} value={z}>
                                  {`${Math.round(z * 100)}%`}
                                </MenuItem>
                              ))}
                            </Select>
                            <Tooltip title={t("zoomIn")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={handleZoomIn}
                                  size="small"
                                  disabled={zoom >= 3.0}
                                >
                                  <ZoomIn />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t("previousPage")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={goToPrevPage}
                                  disabled={pageNumber <= 1}
                                  size="small"
                                >
                                  <ArrowBack />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <MDTypography variant="body2" component="span">
                              {t("page")} {pageNumber} {t("of")} {numPages}
                            </MDTypography>
                            <Tooltip title={t("nextPage")}>
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  onClick={goToNextPage}
                                  disabled={pageNumber >= numPages}
                                  size="small"
                                >
                                  <ArrowForward />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </MDBox>
                          <MDBox display="flex" gap={1}>
                            <Tooltip title={t("deleteDocument")}>
                              <IconButton
                                onClick={() => {
                                  setDeleteTarget({
                                    documentId: id,
                                    companyId: selectedCompany?.id,
                                  });
                                  setDeleteConfirmOpen(true);
                                }}
                                color="error"
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("downloadDocument")}>
                              <IconButton onClick={handleDownload} size="small">
                                <Download />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("printDocument")}>
                              <IconButton onClick={handlePrint} size="small">
                                <Print />
                              </IconButton>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                        <MDBox
                          sx={{
                            height: isMdDown ? "60vh" : "calc(100vh - 280px)",
                            overflow: "auto",
                            backgroundColor: "background.paper",
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            p: 2,
                          }}
                          id="pdf-container"
                        >
                          {documentData.file_url ? (
                            <Document
                              key={id}
                              file={documentData.file_url}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={(error) =>
                                console.error("Error loading document:", error)
                              }
                            >
                              <div style={{ position: "relative", display: "inline-block" }}>
                                <Page
                                  pageNumber={pageNumber}
                                  width={
                                    ((containerWidth || Math.min(window.innerWidth, 800)) - 40) *
                                    zoom
                                  }
                                />
                              </div>
                            </Document>
                          ) : (
                            <MDTypography variant="body1" color="error">
                              {t("documentFileUrlMissing")}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (deleteMutation.isPending) return;
          setDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("deleteDocument")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("confirmDeleteDocument")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteTarget(null);
            }}
            disabled={deleteMutation.isPending}
          >
            {t("cancel")}
          </MDButton>
          <MDButton
            variant="contained"
            color="error"
            onClick={() => {
              if (
                deleteTarget?.documentId !== id ||
                deleteTarget?.companyId !== selectedCompany?.id
              ) {
                showSnackbar(
                  t("companyChanged") || "Document context changed. Reopen the dialog.",
                  "error"
                );
                setDeleteConfirmOpen(false);
                setDeleteTarget(null);
                return;
              }
              deleteMutation.mutate(deleteTarget.documentId);
            }}
            disabled={deleteMutation.isPending}
            aria-label={t("delete")}
          >
            {deleteMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t("delete")
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default DocumentViewer;
